import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Camera, CameraState } from 'types/camera';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';
import { TagParams } from 'tags/types/tagged';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useUser } from 'hooks/useAuth';
import { updateWorkspaceBooleanTrait } from 'services/customer_tracking';

export type UseCamerasParams = TagParams & {
  statuses?: CameraState[];
  camera_ids?: string[];
  camera_models?: string[];
  camera_names?: string[];
  gateway_ids?: string[];
  gateway_names?: string[];
  /** ISO date string */
  created_ts_since?: string;
  /** ISO date string */
  created_ts_until?: string;
  /** ISO date string */
  updated_ts_since?: string;
  /** ISO date string */
  updated_ts_until?: string;
};

type UseCamerasOptions = UseQueryOptions<OffsetPaginated<Camera>, APIError> & {
  params?: UseCamerasParams & OffsetPaginationParams;
};

export function useCameras({
  queryKey = ['cameras'],
  params,
  ...options
}: UseCamerasOptions = {}) {
  const { applicationID, client } = useAPI();
  const user = useUser();

  const queryParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      const { data } = await client.get(`/apps/${applicationID}/cameras`, {
        params: queryParams,
      });

      return data;
    },
    {
      ...options,
      onSuccess: (cameras) => {
        if (!user) {
          return;
        }

        updateWorkspaceBooleanTrait({
          userId: user.id,
          workspaceId: applicationID,
          trait: 'has_camera',
          value: cameras.data.length > 0,
        });
      },
    }
  );
}
